import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';

import { SettingsEditableValue } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useTenantLogo, useTenantName } from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

const EditTenantNameDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingName, setPendingName] = useState(tenantName ?? '');

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error('Failed to update organization name');
    },
    optimisticResponse: (data) => ({
      updateTenantsByPk: {
        __typename: 'Tenants' as const,
        id: tenantId,
        name: data.name ?? '',
        logoUrl: tenantLogoUrl,
      },
    }),
  });

  useEffect(() => {
    if (open) {
      setPendingName(tenantName ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    try {
      setIsUpdating(true);

      await updateTenantDetails({
        variables: {
          tenantId,
          name: pendingName,
          logoUrl: tenantLogoUrl,
        },
      });

      onOpenChange(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Edit Organization Name</DialogTitle>
        </DialogHeader>
        <Input
          value={pendingName}
          onChange={(e) => setPendingName(e.target.value)}
          placeholder="Enter organization name"
          aria-label="Organization name"
        />
        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            disabled={isUpdating}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={isUpdating || !pendingName || pendingName === tenantName}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TenantNameSettings: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);

  return (
    <SettingsEditableValue
      value={tenantName ?? ''}
      editLabel="Edit organization name"
      renderDialog={(props) => <EditTenantNameDialog {...props} />}
    />
  );
};
